/**
 * Konverteringshändelser till GTM (dataLayer).
 *
 * Alla steg i sajtens trattar skickas härifrån med samma struktur så att
 * GA4, Google Ads och Meta Pixel kan triggas på exakt samma händelser.
 * Händelsenamnen följer de namn som redan finns i GA4 och GTM
 * (initiate_search, view_search_result, view_agreement, signed_agreement,
 * initiate_solar_request …) så att historiken i GA4 fortsätter, plus GA4:s
 * rekommenderade namn (generate_lead, begin_checkout, sign_up) där de passar.
 *
 * - `event_id` är unikt per händelse och används för deduplicering (Meta,
 *   framtida server-side/Conversions API).
 * - `transaction_id` sätts på slutförda ordrar så att Google Ads räknar
 *   varje order en gång även om tacksidan laddas om.
 * - Kontaktuppgifter skickas endast SHA-256-hashade (`user_data`) för Google
 *   Ads förbättrade konverteringar och Meta Advanced Matching. Taggarna som
 *   läser dem kräver marknadsföringssamtycke i GTM.
 *
 * Allt är fail-safe – spårningen får aldrig krascha eller blockera UI:t.
 */

export type ConversionEventName =
  // Elavtal privat
  | "initiate_search"
  | "view_search_result"
  | "select_offer"
  | "view_agreement"
  | "begin_checkout"
  | "signed_agreement"
  // Elavtal företag/BRF
  | "initiate_search_b2b"
  | "view_search_result_b2b"
  | "business_quote_start"
  // Offertförfrågningar (solceller, batterier, värmepumpar, laddboxar …)
  | "quote_start"
  | "quote_step"
  | "initiate_solar_request"
  | "solar_installation_details"
  | "solar_address_entered"
  | "completed_solar_request"
  // Leads (alla formulär som skapar ett lead)
  | "generate_lead"
  | "contact_form_submit"
  | "kontaktformular_inskickad"
  | "partner_application"
  | "power_of_attorney_signed"
  | "waitlist_signup"
  | "newsletter_signup"
  | "app_store_click";

export interface ConversionParams {
  customer_type?: "private" | "business" | null;
  search_flow?: string | null;
  elomrade?: string | null;
  consumption_kwh?: number | null;
  contract_type?: string | null;
  company_name?: string | null;
  company_kind?: string | null;
  offer_source?: string | null;
  position?: number | null;
  checkout_step?: number | null;
  transaction_id?: string | null;
  lead_type?: string | null;
  lead_category?: "quote" | "contact" | "partner" | "other" | null;
  services?: string | null;
  quote_step?: string | null;
  form_name?: string | null;
  link_location?: string | null;
  store?: "app_store" | "google_play" | null;
  campaign?: string | null;
  value?: number | null;
  currency?: string | null;
}

/** Kontaktuppgifter i klartext – hashas innan de lämnar funktionen. */
export interface ConversionUser {
  email?: string | null | undefined;
  phone?: string | null | undefined;
}

/**
 * Nycklar som nollställs vid varje händelse. GTM:s dataLayer slår ihop alla
 * pushar, så utan detta skulle t.ex. `transaction_id` från en tidigare
 * händelse följa med nästa.
 */
const RESET_KEYS: (keyof ConversionParams | "event_id" | "user_data")[] = [
  "customer_type",
  "search_flow",
  "elomrade",
  "consumption_kwh",
  "contract_type",
  "company_name",
  "company_kind",
  "offer_source",
  "position",
  "checkout_step",
  "transaction_id",
  "lead_type",
  "lead_category",
  "services",
  "quote_step",
  "form_name",
  "link_location",
  "store",
  "campaign",
  "value",
  "currency",
  "event_id",
  "user_data",
];

type DataLayerWindow = Window & { dataLayer?: Record<string, unknown>[] };

const newId = (): string => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

/** Normaliserar e-post enligt Googles och Metas krav (gemener, utan blanksteg). */
export const normalizeEmail = (email: string): string => email.trim().toLowerCase();

/** Normaliserar telefonnummer till E.164 (+46…) för svenska nummer. */
export const normalizePhone = (phone: string): string | null => {
  const digits = phone.replace(/[^\d+]/g, "");
  if (!digits) return null;
  if (digits.startsWith("+")) return digits;
  if (digits.startsWith("00")) return `+${digits.slice(2)}`;
  if (digits.startsWith("0")) return `+46${digits.slice(1)}`;
  if (digits.startsWith("46")) return `+${digits}`;
  return null;
};

export async function sha256Hex(value: string): Promise<string | null> {
  try {
    if (typeof crypto === "undefined" || !crypto.subtle) return null;
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

/** Hashade kontaktuppgifter i det format Google tag och Meta Pixel läser. */
export async function hashUserData(user: ConversionUser): Promise<Record<string, string> | null> {
  const out: Record<string, string> = {};
  if (user.email && user.email.includes("@")) {
    const h = await sha256Hex(normalizeEmail(user.email));
    if (h) out["sha256_email_address"] = h;
  }
  const phone = user.phone ? normalizePhone(user.phone) : null;
  if (phone) {
    const h = await sha256Hex(phone);
    if (h) out["sha256_phone_number"] = h;
  }
  return Object.keys(out).length ? out : null;
}

const clean = (params: ConversionParams): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  }
  return out;
};

const push = (event: ConversionEventName, payload: Record<string, unknown>) => {
  if (typeof window === "undefined") return;
  try {
    const w = window as DataLayerWindow;
    w.dataLayer = w.dataLayer ?? [];
    const reset: Record<string, undefined> = {};
    for (const k of RESET_KEYS) reset[k] = undefined;
    w.dataLayer.push({ ...reset, event, ...payload });
  } catch {
    /* ignorera */
  }
};

/**
 * Skickar en konverteringshändelse. Med `user` hashas kontaktuppgifterna
 * först (asynkront) – händelsen skickas ändå om hashningen misslyckas.
 * Returnerar händelsens `event_id`.
 */
export function trackConversion(
  event: ConversionEventName,
  params: ConversionParams = {},
  user?: ConversionUser,
): string {
  const eventId = newId();
  const base = { ...clean(params), event_id: eventId };
  if (!user || (!user.email && !user.phone)) {
    push(event, base);
    return eventId;
  }
  void hashUserData(user).then(
    (userData) => push(event, userData ? { ...base, user_data: userData } : base),
    () => push(event, base),
  );
  return eventId;
}

/** Skickar en händelse högst en gång per nyckel och sidladdning. */
const sentOnce = new Set<string>();
export function trackConversionOnce(
  key: string,
  event: ConversionEventName,
  params: ConversionParams = {},
): void {
  if (sentOnce.has(key)) return;
  sentOnce.add(key);
  trackConversion(event, params);
}

/** Vilken kategori ett lead tillhör – styr vilka konverteringstaggar som körs. */
export function leadCategoryOf(type: string): NonNullable<ConversionParams["lead_category"]> {
  if (type === "kontakt") return "contact";
  if (type === "partner") return "partner";
  if (type === "spontanansokan" || type === "ai_prognos_vantelista" || type === "forecasts_api_interest") return "other";
  return "quote";
}
